/**
 * URL helper utilities for generating and parsing URLs
 */

/**
 * Generate a slug from a string
 * @param {string} text - The text to convert to slug
 * @returns {string} - The slug
 */
export const generateSlug = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generate ad URL with slug
 * @param {Object} ad - The ad object
 * @param {string} societySlug - Optional society slug
 * @returns {string} - The ad URL
 */
export const getAdUrl = (ad, societySlug = null) => {
  if (societySlug) {
    return `/societies/${societySlug}/ads/${ad.slug || ad.id}`;
  }
  return `/ads/${ad.slug || ad.id}`;
};

/**
 * Generate event URL with slug
 * @param {Object} event - The event object
 * @param {string} societySlug - Optional society slug
 * @returns {string} - The event URL
 */
export const getEventUrl = (event, societySlug = null) => {
  if (societySlug) {
    return `/societies/${societySlug}/events/${event.slug || event.id}`;
  }
  return `/events/${event.slug || event.id}`;
};

export const getEventEditUrl = (event, societySlug = null) => {
  const segment = event?.slug || event?.id;
  if (!segment) return '/events';
  if (societySlug) {
    return `/societies/${societySlug}/events/${segment}/edit`;
  }
  return `/events/${segment}/edit`;
};

/**
 * Generate business URL with slug
 * @param {Object} business - The business object
 * @param {string} societySlug - Optional society slug
 * @returns {string} - The business URL
 */
export const getBusinessUrl = (business, societySlug = null) => {
  if (societySlug) {
    return `/societies/${societySlug}/businesses/${business.slug || business.id}`;
  }
  return `/businesses/${business.slug || business.id}`;
};

/**
 * Generate society URL with slug
 * @param {Object} society - The society object
 * @returns {string} - The society URL
 */
export const getSocietyUrl = (society) => {
  return `/societies/${society.slug || society.id}`;
};

/**
 * Generate society-specific page URL
 * @param {string} societySlug - The society slug
 * @param {string} page - The page type (ads, events, businesses)
 * @returns {string} - The society page URL
 */
export const getSocietyPageUrl = (societySlug, page) => {
  return `/societies/${societySlug}/${page}`;
};

export const getPostUrl = (post, societySlug = null) => {
  if (societySlug) {
    return `/societies/${societySlug}/posts/${post.slug || post.id}`;
  }
  return `/posts/${post.slug || post.id}`;
};

export const getPropertyUrl = (property, societySlug = null) => {
  if (societySlug) {
    return `/societies/${societySlug}/properties/${property.slug || property.id}`;
  }
  return `/properties/${property.slug || property.id}`;
};

export const getMosqueUrl = (mosque, societySlug = null) => {
  const segment = mosque?.slug || mosque?.id;
  if (!segment) return '/mosques';
  if (societySlug) {
    return `/societies/${societySlug}/mosques/${segment}`;
  }
  return `/mosques/${segment}`;
};

/**
 * Check if current URL is society-specific
 * @param {string} pathname - The current pathname
 * @returns {Object} - Object with isSocietySpecific and societySlug
 */
export const parseSocietyUrl = (pathname) => {
  const societyMatch = pathname.match(/^\/societies\/([^\/]+)/);
  if (societyMatch) {
    return {
      isSocietySpecific: true,
      societySlug: societyMatch[1]
    };
  }
  return {
    isSocietySpecific: false,
    societySlug: null
  };
};

/**
 * Extract slug from URL parameters
 * @param {Object} params - URL parameters
 * @param {string} type - Type of slug (ad, event, business, society)
 * @returns {string} - The slug or ID
 */
export const getSlugFromParams = (params, type) => {
  const slugKey = `${type}Slug`;
  return params[slugKey] || params.id;
};
